"use client";

import type { AutocompleteOption } from "@/components/Autocomplete/types";
import type { AutocompleteFetchFunction } from "@/components/form/types";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { useAutocompleteOptions } from "@/hooks/useAutocompleteOptions";
import useDebounce from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

interface AsyncAutocompleteSingleSelectProps {
  fetchOptions: AutocompleteFetchFunction;
  queryKey: string[];
  limit?: number;
  debounce?: number;

  /** Pre-selected option for edit mode — prevents empty input on load. */
  defaultOption?: AutocompleteOption;

  value: string | null;
  onValueChange: (value: string | null) => void;

  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  showClear?: boolean;

  className?: {
    inputClass?: string;
    contentClass?: string;
    emptyClass?: string;
    itemClass?: string;
    listClass?: string;
  };

  id?: string;
  name?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  "aria-required"?: boolean;
  onBlur?: () => void;
}

/**
 * Async single-select autocomplete — fetches options from a server as the user types.
 *
 * - Debounces search input before fetching (default 300ms)
 * - Server handles filtering; Base UI's local filter is disabled via `filter={null}`
 * - Merges `defaultOption` so edit mode shows the selected label immediately
 * - Shows a spinner while fetching
 *
 * Does not know about forms. Use `AsyncAutocompleteSingleSelectField` for RHF.
 */
function AsyncAutocompleteSingleSelect({
  fetchOptions,
  queryKey,
  limit = 20,
  debounce: debounceMs = 300,
  defaultOption,
  value,
  onValueChange,
  placeholder = "Search...",
  emptyMessage = "No options found.",
  disabled = false,
  showClear = true,
  className,
  id,
  name,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  onBlur,
}: AsyncAutocompleteSingleSelectProps) {
  const { inputClass, contentClass, emptyClass, listClass, itemClass } =
    className ?? {};

  // Controls what the input displays — decoupled from the selected value.
  // Initialized from defaultOption so edit mode shows the label immediately.
  const [localInputValue, setLocalInputValue] = useState(
    defaultOption?.label ?? ""
  );
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, debounceMs);

  const { options: fetchedOptions, isLoading, isFetching, isError } = useAutocompleteOptions({
    queryKey,
    fetchFn: fetchOptions,
    search: debouncedSearch,
    limit,
    enabled: !disabled,
  });

  // Prepend defaultOption only if it's not already in the fetched list.
  // This ensures edit mode shows the label immediately without a duplicate.
  const options = useMemo(() => {
    if (!defaultOption) return fetchedOptions;
    const alreadyPresent = fetchedOptions.some(
      (o) => o.value === defaultOption.value
    );
    return alreadyPresent ? fetchedOptions : [defaultOption, ...fetchedOptions];
  }, [fetchedOptions, defaultOption]);

  const optionMap = useMemo(
    () => new Map(options.map((o) => [o.value, o])),
    [options]
  );

  const selectedOption = value !== null ? (optionMap.get(value) ?? null) : null;

  const handleValueChange = (selected: AutocompleteOption | null) => {
    onValueChange(selected?.value ?? null);
    setLocalInputValue(selected?.label ?? "");
    setSearchTerm("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setLocalInputValue(text);
    setSearchTerm(text);
    // When the user edits the input, clear the current selection.
    // Without this, Base UI would keep resetting the input back to the selected label.
    if (value !== null) {
      onValueChange(null);
    }
  };

  return (
    <Combobox<AutocompleteOption>
      items={options}
      filter={null}
      inputValue={localInputValue}
      itemToStringValue={(option) => option.label}
      value={selectedOption}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <ComboboxInput
        id={id}
        name={name}
        placeholder={placeholder}
        showClear={showClear}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        onBlur={onBlur}
        onChange={handleInputChange}
        disabled={disabled}
        className={cn("w-full", inputClass)}
      />

      <ComboboxContent className={cn(contentClass)}>
        {isLoading || isFetching ? (
          <div className="text-muted-foreground flex items-center justify-center gap-2 p-4 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading...</span>
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center gap-2 p-4 text-sm text-destructive">
            <span>Failed to load options. Please try again.</span>
          </div>
        ) : (
          <>
            <ComboboxEmpty className={cn(emptyClass)}>
              {emptyMessage}
            </ComboboxEmpty>

            <ComboboxList className={cn(listClass)}>
              {(option: AutocompleteOption) => (
                <ComboboxItem
                  key={option.value}
                  value={option}
                  disabled={option.disabled}
                  className={cn(
                    option.disabled && "cursor-not-allowed",
                    itemClass
                  )}
                >
                  {option.label}
                </ComboboxItem>
              )}
            </ComboboxList>
          </>
        )}
      </ComboboxContent>
    </Combobox>
  );
}

export default AsyncAutocompleteSingleSelect;
